import { FC, ReactNode, useCallback, useMemo } from 'react';
import useApp from '@/hooks/use-app.hook';
import { has } from 'lodash';
import { PolicyModel } from '@/lib/models/policy.model';
import useAppData from '@/hooks/use-app-data.hook';
import { FeatureContext, FeatureFlags } from './helpers';
import { canAccessAdminUI } from '@/lib/services/auth.service';

interface Props {
  children: ReactNode;
}

export const FeatureProvider: FC<Props> = ({ children }) => {
  const { signedIn } = useApp();

  const userPolicyQuery = useAppData<PolicyModel>('portal-policy', { enabled: signedIn });
  const { data: userPolicy, isLoading: userPolicyLoading } = userPolicyQuery;

  const flags: FeatureFlags = useMemo(() => {
    const canUseChat = signedIn && !!userPolicy?.portal?.enabled;

    return {
      canUseChat: signedIn && !!userPolicy?.portal?.enabled,
      canViewHistory: canUseChat && !!userPolicy?.portal?.historyEnabled,
      canViewAdminUI: canAccessAdminUI(),
    };
  }, [signedIn, userPolicy]);

  const hasFeature = useCallback(
    (featureFlagVal: string): boolean => {
      if (!has(flags, featureFlagVal)) {
        return false;
      }

      return !!flags[featureFlagVal];
    },
    [flags]
  );

  return (
    <FeatureContext.Provider
      value={{
        ...flags,
        hasFeature,
        loading: userPolicyLoading,
      }}
    >
      {children}
    </FeatureContext.Provider>
  );
};
